import { getPremiumIncreaseThresholdPercent } from '@common/utils';
import { isEmpty, isEqual } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { AIFeature } from 'enums';
import useLoadFilesSelection from 'broker/pages/SubmissionWorkspacePage/components/Toolbox/components/Files/useLoadFilesSelection';
import useSubmissionsWorkspace from 'broker/pages/SubmissionWorkspacePage/store/useSubmissionWorkspace';
import { useUiStoreActions, useUiStoreState } from 'broker/pages/SubmissionWorkspacePage/ui-store/uiStoreProvider';

interface UseAIAssistantTriggerListenerProps {
  assistantButtonRef: { current: HTMLButtonElement | null };
  onTrigger: () => void;
}

interface UseAIAssistantTriggerListenerResult {
  initialQuestion: string | undefined;
  aiFeature: AIFeature | undefined;
  quoteId: string | undefined;
  resetTriggerState: () => void;
}

export function useAIAssistantTriggerListener({
  assistantButtonRef,
  onTrigger,
}: UseAIAssistantTriggerListenerProps): UseAIAssistantTriggerListenerResult {
  const { filesExplorer, aiAssistantTriggerParams, aiAssistantActiveFeature } = useUiStoreState();
  const { clearAIAssistantTrigger } = useUiStoreActions();
  const { submission } = useSubmissionsWorkspace();

  const [initialQuestion, setInitialQuestion] = useState<string | undefined>(undefined);
  const [aiFeature, setAiFeature] = useState<AIFeature | undefined>(undefined);
  const [quoteId, setQuoteId] = useState<string | undefined>(undefined);
  const [preSelectedFileIds, setPreSelectedFileIds] = useState<string[] | undefined>(undefined);

  const { resetLoadingFileState } = useLoadFilesSelection({
    preSelectedFileIds,
  });

  const resetTriggerState = useCallback(() => {
    setInitialQuestion(undefined);
    setAiFeature(undefined);
    setQuoteId(undefined);
    setPreSelectedFileIds(undefined);
    // This is needed so we can reload the files selection on the next trigger
    resetLoadingFileState();
  }, [resetLoadingFileState]);

  // For built in features, we show the user a simplified version of the prompt we are actually running to indicate the feature purpose
  const getUserFacingQuestion = useCallback(
    (feature?: AIFeature) => {
      const thresholdPercent = getPremiumIncreaseThresholdPercent(submission);

      switch (feature) {
        case AIFeature.QuoteToExpiringCompare:
          return (
            `Please compare the Expiring Policy and Renewal Quote and let me know if both conditions are met:\n` +
            `1. The premium of the Renewal Quote increase isn't ${thresholdPercent}% higher than the Expiring Policy's.\n` +
            `2. The aggregate limit of the Renewal Quote is the same as the Expiring Policy's.`
          );
        case AIFeature.ActualPremiumCalculation:
          return `Please calculate the actual premium of the expiring policy based on the expiring policy and expiring endorsement files`;
        default:
          return undefined;
      }
    },
    [submission],
  );

  // Watch for trigger from UI store
  useEffect(() => {
    if (aiAssistantTriggerParams.fileIds && (aiAssistantTriggerParams.question || aiAssistantActiveFeature)) {
      setInitialQuestion(aiAssistantTriggerParams.question ?? getUserFacingQuestion(aiAssistantActiveFeature));
      setAiFeature(aiAssistantActiveFeature);
      setQuoteId(aiAssistantTriggerParams.quoteId);
      setPreSelectedFileIds(aiAssistantTriggerParams.fileIds);
      onTrigger();

      // Clear the trigger after processing
      clearAIAssistantTrigger();
    }
  }, [
    aiAssistantTriggerParams,
    aiAssistantActiveFeature,
    clearAIAssistantTrigger,
    onTrigger,
    setPreSelectedFileIds,
    getUserFacingQuestion,
  ]);

  // Watch for files to be loaded before actually opening the popup
  useEffect(() => {
    if (
      filesExplorer.isLoaded &&
      !isEmpty(preSelectedFileIds) &&
      isEqual(
        preSelectedFileIds,
        filesExplorer.selectedFiles.map((file) => file.id),
      )
    ) {
      // clear after the first time so we won't run this again
      setPreSelectedFileIds(undefined);

      // Open the popup
      assistantButtonRef.current?.click();
    }
  }, [
    preSelectedFileIds,
    filesExplorer.isLoaded,
    filesExplorer.selectedFiles,
    assistantButtonRef,
    setPreSelectedFileIds,
  ]);

  return {
    initialQuestion,
    resetTriggerState,
    aiFeature,
    quoteId,
  };
}
